<template>
  <div v-if="visible" class="modal-overlay">
    <div class="modal-container modal-full-width">
      <div
        class="modal-header"
        :class="{
          'alerta-vermelho': alertaNivel === 'vermelho',
          'alerta-amarelo': alertaNivel === 'amarelo',
          'alerta-verde': alertaNivel === 'verde',
          'alerta-roxo': alertaNivel === 'roxo'
        }"
      >
        <h6>
          Protocolo de Queda — {{ local?.aluno_nome || 'Aluno' }}
          <span class="badge bg-secondary ms-2">{{ salvando ? 'Salvando…' : resultadoIncerto ? 'Não confirmado' : (local?.status || 'RASCUNHO') }}</span>
        </h6>
        <div v-if="alertaNivel !== 'normal'" class="alerta-banner">
          <span v-if="alertaNivel === 'vermelho'">
            🔴 Alerta Vermelho — contato imediato com supervisor (ligação obrigatória)
          </span>
          <span v-else-if="alertaNivel === 'amarelo'">
            🟡 Alerta Amarelo — comunicar supervisor
          </span>
          <span v-else-if="alertaNivel === 'roxo'">
            🟣 Restrição — aluno NÃO pode montar novamente nesta aula
          </span>
          <span v-else-if="alertaNivel === 'verde'">
            🟢 Alerta Verde — queda sem dor relatada, aluno pode montar novamente
          </span>
        </div>

        <i class="bi bi-x-circle close-icon" @click="!salvando && $emit('close')"></i>
      </div>

      <div class="modal-content">
        <!-- Identificador de tipo de aula -->
        <div
          v-if="local?.aula_experimental || local?.aula_avulsa || local?.aula_reposicao"
          class="tipo-aula-banner"
          :class="{
            'tipo-experimental': local?.aula_experimental,
            'tipo-avulsa': local?.aula_avulsa,
            'tipo-reposicao': local?.aula_reposicao
          }"
        >
          <span v-if="local?.aula_experimental">Aula Experimental</span>
          <span v-else-if="local?.aula_avulsa">Aula Avulsa</span>
          <span v-else-if="local?.aula_reposicao">Aula de Reposição</span>
        </div>

        <div class="pq-meta mb-2">
          <div>
            <strong>Data:</strong>
            {{ formatarDataBR(local?.protocolo_json?.aula?.data_aula) }}
          </div>

          <div>
            <strong>Turma:</strong>
            {{ local?.protocolo_json?.aula?.turma || '—' }}
            |
            {{ capitalizar(local?.protocolo_json?.aula?.dia_semana) }}
            |
            {{ formatarHora(local?.protocolo_json?.aula?.horario) }}
          </div>

          <div>
            <strong>Instrutor:</strong>
            {{ local?.protocolo_json?.aula?.instrutor_nome || '—' }}
          </div>

          <div>
            <strong>Animal:</strong>
            {{ animalNome }}
          </div>

          <div>
            <strong>Responsável: </strong>
            <span v-if="local?.protocolo_json?.aluno?.responsavel_nome_snapshot">
              {{ local.protocolo_json.aluno.responsavel_nome_snapshot }}
              <span v-if="local?.protocolo_json?.aluno?.responsavel_celular_snapshot">
                • {{ formatarTelefoneSnapshot(local.protocolo_json.aluno.responsavel_celular_snapshot, local.protocolo_json.aluno.responsavel_celular_ddi_snapshot) }}
              </span>
              <span v-else class="text-muted">
                • Telefone não informado
              </span>
            </span>
            <span v-else>—</span>
          </div>
        </div>

        <hr />

        <!-- ALERTA VERMELHO -->
        <h6 class="section-title">Sinais de alerta vermelho</h6>
        <div class="check-grid severidade-vermelho">
          <label
            v-for="opt in dict.vermelho"
            :key="opt.code"
            class="check-item"
            :class="{
              'is-checked': local.protocolo_json.checklist.alerta_vermelho.itens?.includes(opt.code),
              'is-readonly': readOnly
            }"
          >
            <input
              type="checkbox"
              :disabled="!canEditInstrutor"
              :value="opt.code"
              v-model="local.protocolo_json.checklist.alerta_vermelho.itens"
            />
            <span>{{ opt.label }}</span>
          </label>
        </div>

        <hr />

        <!-- ALERTA AMARELO -->
        <h6 class="section-title">Sinais de alerta amarelo</h6>
        <div class="check-grid severidade-amarelo">
          <label
            v-for="opt in dict.amarelo"
            :key="opt.code"
            class="check-item"
            :class="{
              'is-checked': local.protocolo_json.checklist.alerta_amarelo.itens?.includes(opt.code),
              'is-readonly': readOnly
            }"
          >
            <input
              type="checkbox"
              :disabled="!canEditInstrutor"
              :value="opt.code"
              v-model="local.protocolo_json.checklist.alerta_amarelo.itens"
            />
            <span>{{ opt.label }}</span>
          </label>
        </div>

        <hr />

        <!-- ALERTA VERDE -->
        <h6 class="section-title">Alerta verde: pode montar novamente</h6>
        <div class="check-grid severidade-verde">
          <label
            v-for="opt in dict.verde"
            :key="opt.code"
            class="check-item"
            :class="{
              'is-checked': local.protocolo_json.checklist.alerta_verde_pode_montar.itens?.includes(opt.code),
              'is-readonly': readOnly
            }"
          >
            <input
              type="checkbox"
              :disabled="!canEditInstrutor"
              :value="opt.code"
              v-model="local.protocolo_json.checklist.alerta_verde_pode_montar.itens"
            />
            <span>{{ opt.label }}</span>
          </label>
        </div>

        <hr />

        <!-- NÃO PODE MONTAR -->
        <h6 class="section-title">Queda: aluno não pode montar novamente</h6>
        <div class="check-grid severidade-roxo">
          <label
            v-for="opt in dict.naoMontar"
            :key="opt.code"
            class="check-item"
            :class="{
              'is-checked': local.protocolo_json.checklist.queda_nao_pode_montar.itens?.includes(opt.code),
              'is-readonly': readOnly
            }"
          >
            <input
              type="checkbox"
              :disabled="!canEditInstrutor"
              :value="opt.code"
              v-model="local.protocolo_json.checklist.queda_nao_pode_montar.itens"
            />
            <span>{{ opt.label }}</span>
          </label>
        </div>

        <hr />

        <!-- FORA DE AULA -->
        <h6 class="section-title">Situações fora de aula</h6>
        <div class="check-grid">
          <label
            v-for="opt in dict.foraAula"
            :key="opt.code"
            class="check-item"
            :class="{
              'is-checked': local.protocolo_json.checklist.situacoes_fora_de_aula.itens?.includes(opt.code),
              'is-readonly': readOnly
            }"
          >
            <input
              type="checkbox"
              :disabled="!canEditInstrutor"
              :value="opt.code"
              v-model="local.protocolo_json.checklist.situacoes_fora_de_aula.itens"
            />
            <span>{{ opt.label }}</span>
          </label>
        </div>

        <hr />

        <div class="form-group">
          <label><strong>Descrição do ocorrido</strong> <span class="required">*</span></label>
          <textarea
            class="form-control pq-textarea"
            rows="4"
            :disabled="!canEditInstrutor"
            v-model="local.protocolo_json.descricao_ocorrido"
          ></textarea>
        </div>

        <hr v-if="workflow" />

        <!-- BLOCO RECEPÇÃO / SUPERVISOR -->
        <div v-if="workflow && local?.protocolo_json?.recepcao">
          <h6 class="section-title">Recepção / Supervisor</h6>

          <div class="row g-2">
            <div class="col-12 col-md-6">
              <div class="pq-meta">
                <div class="mb-2"><strong>Comunicado ao supervisor</strong></div>

                <div class="form-check">
                  <input
                    class="form-check-input"
                    type="checkbox"
                    :disabled="!canEditComunicadoSupervisor"
                    :checked="local.protocolo_json.recepcao.comunicado_supervisor.realizado"
                    @change="onToggleRealizado('comunicado_supervisor', $event.target.checked)"
                  />
                  <label class="form-check-label ms-2">Realizado</label>
                </div>

                <div class="mt-2">
                  <label class="form-label">Meio</label>
                  <select
                    class="form-control"
                    :disabled="!canEditComunicadoSupervisor"
                    v-model="local.protocolo_json.recepcao.comunicado_supervisor.meio"
                  >
                    <option :value="null">Selecione</option>
                    <option value="WHATSAPP">WhatsApp</option>
                    <option value="LIGACAO">Ligação</option>
                    <option value="PESSOALMENTE">Pessoalmente</option>
                  </select>
                </div>

                <div class="mt-2">
                  <label class="form-label">Observação</label>
                  <textarea
                    class="form-control pq-textarea"
                    rows="4"
                    :disabled="!canEditComunicadoSupervisor"
                    v-model="local.protocolo_json.recepcao.comunicado_supervisor.observacao"
                  ></textarea>
                </div>

                <div
                  class="small text-muted mt-2"
                  v-if="local.protocolo_json.recepcao.comunicado_supervisor.realizado_em"
                >
                  Realizado em:
                  {{ new Date(local.protocolo_json.recepcao.comunicado_supervisor.realizado_em).toLocaleString('pt-BR') }}
                  <span v-if="local.protocolo_json.recepcao.comunicado_supervisor.por">
                    • por: {{ local.protocolo_json.recepcao.comunicado_supervisor.por }}
                  </span>
                </div>
              </div>
            </div>

            <div class="col-12 col-md-6">
              <div class="pq-meta">
                <div class="mb-2"><strong>Contato com responsável</strong></div>

                <div class="mb-2">
                  <div class="small text-muted">Responsável</div>
                  <div>
                    <strong>{{ local?.protocolo_json?.aluno?.responsavel_nome_snapshot || '—' }}</strong>
                    <span class="text-muted" v-if="local?.protocolo_json?.aluno?.responsavel_celular_snapshot">
                      • {{ formatarTelefoneSnapshot(local.protocolo_json.aluno.responsavel_celular_snapshot, local.protocolo_json.aluno.responsavel_celular_ddi_snapshot) }}
                    </span>
                  </div>
                </div>

                <div class="form-check">
                  <input
                    class="form-check-input"
                    type="checkbox"
                    :disabled="!canEditContatoResponsavel"
                    :checked="local.protocolo_json.recepcao.contato_responsavel.realizado"
                    @change="onToggleRealizado('contato_responsavel', $event.target.checked)"
                  />
                  <label class="form-check-label ms-2">Realizado</label>
                </div>

                <div class="mt-2">
                  <label class="form-label">Meio</label>
                  <select
                    class="form-control"
                    :disabled="!canEditContatoResponsavel"
                    v-model="local.protocolo_json.recepcao.contato_responsavel.meio"
                  >
                    <option :value="null">Selecione</option>
                    <option value="WHATSAPP">WhatsApp</option>
                    <option value="LIGACAO">Ligação</option>
                    <option value="PESSOALMENTE">Pessoalmente</option>
                  </select>
                </div>

                <div class="mt-2">
                  <label class="form-label">Observação</label>
                  <textarea
                    class="form-control pq-textarea"
                    rows="4"
                    :disabled="!canEditContatoResponsavel"
                    v-model="local.protocolo_json.recepcao.contato_responsavel.observacao"
                  ></textarea>
                </div>

                <div
                  class="small text-muted mt-2"
                  v-if="local.protocolo_json.recepcao.contato_responsavel.realizado_em"
                >
                  Realizado em:
                  {{ new Date(local.protocolo_json.recepcao.contato_responsavel.realizado_em).toLocaleString('pt-BR') }}
                  <span v-if="local.protocolo_json.recepcao.contato_responsavel.por">
                    • por: {{ local.protocolo_json.recepcao.contato_responsavel.por }}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <hr class="mt-3" />

          <h6 class="section-title">Assinaturas</h6>

          <div class="pq-meta">
            <div class="mb-1">
              <strong>Instrutor: </strong>
              <span v-if="local.assinaturas_json?.instrutor?.assinado_em">
                {{ local.assinaturas_json.instrutor.nome || '—' }} —
                {{ new Date(local.assinaturas_json.instrutor.assinado_em).toLocaleString('pt-BR') }}
              </span>
              <span v-else class="text-muted">Pendente</span>
            </div>

            <div class="mb-1">
              <strong>Recepção: </strong>
              <span v-if="local.assinaturas_json?.recepcao?.assinado_em">
                {{ local.assinaturas_json.recepcao.nome || '—' }} —
                {{ new Date(local.assinaturas_json.recepcao.assinado_em).toLocaleString('pt-BR') }}
              </span>
              <span v-else class="text-muted">Pendente</span>
            </div>

            <div>
              <strong>Supervisor: </strong>
              <span v-if="local.assinaturas_json?.supervisor?.assinado_em">
                {{ local.assinaturas_json.supervisor.nome || '—' }} —
                {{ new Date(local.assinaturas_json.supervisor.assinado_em).toLocaleString('pt-BR') }}
              </span>
              <span v-else class="text-muted">Pendente</span>
            </div>
          </div>
        </div>
      </div>

      <div v-if="erroPersistencia" class="alert alert-warning" role="alert">{{ erroPersistencia }}</div>
      <div class="modal-footer">
        <button class="btn btn-secondary me-2" :disabled="salvando" @click="!salvando && $emit('close')">Fechar</button>

        <template v-if="!readOnly">
          <button
            class="btn btn-success"
            :disabled="!canEnviarRecepcao"
            @click="assinarEEnviarRecepcao"
          >
            Assinar e enviar à recepção
          </button>
        </template>

        <!-- Botões do workflow (Recepção/Supervisor) -->
        <template v-if="workflow && readOnly">
          <!-- Botão Recepção -->
          <template v-if="isRecepcao || isAdmin">
            <button
              class="btn btn-outline-primary"
              :disabled="salvando || resultadoIncerto || !canAssinarEEnviarRecepcaoWorkflow"
              @click="assinarEEnviarRecepcaoWorkflow"
            >
              Assinar e enviar
            </button>
          </template>

          <!-- Botão Supervisor -->
          <template v-if="isSupervisor || isAdmin">
            <button
              class="btn btn-outline-dark"
              :disabled="salvando || resultadoIncerto || !canAssinarSupervisor"
              @click="assinarSupervisor"
            >
              Assinar (Supervisor)
            </button>
          </template>
        </template>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, reactive, ref, watch, getCurrentInstance } from 'vue';
import axios from '@/services/axios';
import { criarPersistenciaQueda, snapshotProtocolo } from '@/utils/protocoloQuedaPersistencia';

const props = defineProps({
  visible: { type: Boolean, default: false },
  registro: { type: Object, default: null },
  readOnly: { type: Boolean, default: false },
  workflow: { type: Boolean, default: false },
  saveToBackend: { type: Boolean, default: false },
  persistir: { type: Function, default: null }
});

const emit = defineEmits(['close', 'save', 'salvou']);

const instance = getCurrentInstance();
const gs = instance?.appContext?.config?.globalProperties?.$globalState;

const local = reactive({});
const salvando = ref(false);
const erroPersistencia = ref('');
const resultadoIncerto = ref(false);
const persistirQueda = criarPersistenciaQueda(axios);
let original = null;

watch(
  () => props.registro,
  async (r) => {
    if (!r) return;
    original = r._snapshotProtocolo || snapshotProtocolo(r);
    Object.keys(local).forEach(key => delete local[key]);
    Object.assign(local, JSON.parse(JSON.stringify(r)));
    erroPersistencia.value = '';
    resultadoIncerto.value = false;
    ensureDefaults();
    await carregarResponsavelSnapshot();
  },
  { immediate: true }
);

const perfil = computed(() => String(gs?.userProfile || '').toLowerCase().trim());
const userName = computed(() => String(gs?.userName || '').trim());

const isInstrutor = computed(() =>
  ['instrutor', 'instrutor supervisor', 'administrador'].includes(perfil.value)
);

const isSupervisor = computed(() =>
  ['instrutor supervisor', 'administrador'].includes(perfil.value)
);

const isRecepcao = computed(() =>
  ['recepção', 'recepcao', 'administrador'].includes(perfil.value)
);

const isAdmin = computed(() => perfil.value === 'administrador');
const canWorkflow = computed(() => props.workflow === true);

// Instrutor
const canEditInstrutor = computed(() => {
  if (props.readOnly || salvando.value || resultadoIncerto.value) return false;
  return isInstrutor.value && local.status === 'RASCUNHO';
});

const canEnviarRecepcao = computed(() => {
  return canEditInstrutor.value && String(local?.protocolo_json?.descricao_ocorrido || '').trim().length > 0;
});

// Alertas
const hasAlertaVermelho = computed(() =>
  (local?.protocolo_json?.checklist?.alerta_vermelho?.itens || []).length > 0
);

const hasAlertaAmarelo = computed(() =>
  (local?.protocolo_json?.checklist?.alerta_amarelo?.itens || []).length > 0
);

const hasNaoPodeMontar = computed(() =>
  (local?.protocolo_json?.checklist?.queda_nao_pode_montar?.itens || []).length > 0
);

const hasAlertaVerde = computed(() =>
  (local?.protocolo_json?.checklist?.alerta_verde_pode_montar?.itens || []).length > 0
);

const alertaNivel = computed(() => {
  if (hasAlertaVermelho.value) return 'vermelho';
  if (hasAlertaAmarelo.value) return 'amarelo';
  if (hasNaoPodeMontar.value) return 'roxo';
  if (hasAlertaVerde.value) return 'verde';
  return 'normal';
});

// Workflow novo
const canEditComunicadoSupervisor = computed(() => {
  if (!canWorkflow.value) return false;

  const st = String(local.status || '');
  return (
    (isRecepcao.value || isAdmin.value) &&
    st === 'ENVIADO_RECEPCAO' &&
    !local?.assinaturas_json?.recepcao?.assinado_em
  );
});

const canEditContatoResponsavel = computed(() => {
  if (!canWorkflow.value) return false;

  const st = String(local.status || '');
  return (
    (isSupervisor.value || isAdmin.value) &&
    st === 'AGUARDANDO_ASSINATURAS' &&
    !!local?.assinaturas_json?.recepcao?.assinado_em &&
    !local?.assinaturas_json?.supervisor?.assinado_em
  );
});

const comunicadoOk = computed(() => {
  const cs = local?.protocolo_json?.recepcao?.comunicado_supervisor;
  if (!cs?.realizado) return false;
  if (!cs?.meio) return false;

  if (hasAlertaVermelho.value && String(cs.meio).toUpperCase() !== 'LIGACAO') return false;

  return true;
});

const contatoOk = computed(() => {
  const cr = local?.protocolo_json?.recepcao?.contato_responsavel;
  if (!cr?.realizado) return false;
  if (!cr?.meio) return false;
  return true;
});

const canAssinarEEnviarRecepcaoWorkflow = computed(() => {
  if (!canEditComunicadoSupervisor.value) return false;
  if (!comunicadoOk.value) return false;
  return !local?.assinaturas_json?.recepcao?.assinado_em;
});

const canAssinarSupervisor = computed(() => {
  if (!canWorkflow.value) return false;
  if (!(isSupervisor.value || isAdmin.value)) return false;

  const st = String(local.status || '');
  if (st !== 'AGUARDANDO_ASSINATURAS') return false;

  if (!local?.assinaturas_json?.recepcao?.assinado_em) return false;
  if (!contatoOk.value) return false;
  if (local?.assinaturas_json?.supervisor?.assinado_em) return false;

  return true;
});

const animalNome = computed(() => {
  return local?.protocolo_json?.aluno?.animal_nome_snapshot || '—';
});

function formatarTelefoneSnapshot(numero, ddi = '') {
  const n = String(numero || '').trim();
  if (!n) return '';
  if (/^\+\d/.test(n)) return n;

  const d = String(ddi || '').trim() || '+55';

  return `${d} ${n}`.trim();
}

function nowISO() {
  return new Date().toISOString();
}

function onToggleRealizado(chave, checked) {
  const recepcao = local?.protocolo_json?.recepcao;
  if (!recepcao || !recepcao[chave]) return;

  const alvo = recepcao[chave];
  alvo.realizado = !!checked;

  if (alvo.realizado) {
    alvo.realizado_em = nowISO();
    alvo.por = userName.value || null;

    if (chave === 'comunicado_supervisor' && hasAlertaVermelho.value) {
      alvo.meio = 'LIGACAO';
    }
  } else {
    alvo.realizado_em = null;
    alvo.por = null;
    alvo.meio = null;
  }
}

function formatarDataBR(dataISO) {
  if (!dataISO) return '—';

  const s = String(dataISO).slice(0, 10);
  const [y, m, d] = s.split('-');
  if (!y || !m || !d) return dataISO;

  return `${d}/${m}/${y}`;
}

function formatarHora(hora) {
  if (!hora) return '—';
  return String(hora).slice(0, 5);
}

function capitalizar(texto) {
  if (!texto) return '—';
  return texto.charAt(0).toUpperCase() + texto.slice(1);
}

async function executarAssinatura(papel, status) {
  if (salvando.value || resultadoIncerto.value) return;
  salvando.value = true;
  erroPersistencia.value = '';
  const antes = snapshotProtocolo(local);
  try {
    local.assinaturas_json[papel] = { assinado: true, nome: userName.value, assinado_em: nowISO() };
    local.status = status;
    await emitSave();
  } catch (error) {
    local.status = antes.status;
    local.assinaturas_json = antes.assinaturas_json;
    erroPersistencia.value = error.message;
    resultadoIncerto.value = error.incerto === true;
  } finally {
    salvando.value = false;
  }
}

async function assinarEEnviarRecepcao() {
  if (canEnviarRecepcao.value) await executarAssinatura('instrutor', 'ENVIADO_RECEPCAO');
}
async function assinarEEnviarRecepcaoWorkflow() {
  if (canAssinarEEnviarRecepcaoWorkflow.value) await executarAssinatura('recepcao', 'AGUARDANDO_ASSINATURAS');
}
async function assinarSupervisor() {
  if (canAssinarSupervisor.value) await executarAssinatura('supervisor', 'CONCLUIDO');
}

function ensureDefaults() {
  if (!local.status) local.status = 'RASCUNHO';

  if (!local.protocolo_json) {
    local.protocolo_json = {
      versao: 1,
      aula: {},
      aluno: {},
      checklist: {},
      descricao_ocorrido: ''
    };
  }

  if (!local.protocolo_json.checklist) local.protocolo_json.checklist = {};
  const ck = local.protocolo_json.checklist;

  if (!ck.alerta_vermelho) ck.alerta_vermelho = { itens: [] };
  if (!ck.alerta_vermelho.itens) ck.alerta_vermelho.itens = [];

  if (!ck.alerta_amarelo) ck.alerta_amarelo = { itens: [] };
  if (!ck.alerta_amarelo.itens) ck.alerta_amarelo.itens = [];

  if (!ck.alerta_verde_pode_montar) ck.alerta_verde_pode_montar = { itens: [] };
  if (!ck.alerta_verde_pode_montar.itens) ck.alerta_verde_pode_montar.itens = [];

  if (!ck.queda_nao_pode_montar) ck.queda_nao_pode_montar = { itens: [] };
  if (!ck.queda_nao_pode_montar.itens) ck.queda_nao_pode_montar.itens = [];

  if (!ck.situacoes_fora_de_aula) ck.situacoes_fora_de_aula = { itens: [] };
  if (!ck.situacoes_fora_de_aula.itens) ck.situacoes_fora_de_aula.itens = [];

  if (local.protocolo_json.descricao_ocorrido == null) {
    local.protocolo_json.descricao_ocorrido = '';
  }

  if (!local.assinaturas_json) {
    local.assinaturas_json = {
      instrutor: { assinado: false, nome: null, assinado_em: null },
      recepcao: { assinado: false, nome: null, assinado_em: null },
      supervisor: { assinado: false, nome: null, assinado_em: null }
    };
  }

  if (!local._tmpId) {
    if (local.id) {
      local._tmpId = `db_${local.id}`;
    } else {
      local._tmpId = `tmp_${Date.now()}_${Math.random().toString(16).slice(2)}`;
    }
  }

  if (!local.protocolo_json.recepcao) {
    local.protocolo_json.recepcao = {
      comunicado_supervisor: { realizado: false, meio: null, realizado_em: null, por: null, observacao: '' },
      contato_responsavel: { realizado: false, meio: null, realizado_em: null, por: null, observacao: '' }
    };
  }

  if (!local.protocolo_json.aluno) local.protocolo_json.aluno = {};

  if (local.protocolo_json.aluno.responsavel_nome_snapshot == null) {
    local.protocolo_json.aluno.responsavel_nome_snapshot = null;
  }
  if (local.protocolo_json.aluno.responsavel_celular_snapshot == null) {
    local.protocolo_json.aluno.responsavel_celular_snapshot = null;
  }
  if (local.protocolo_json.aluno.responsavel_celular_ddi_snapshot == null) {
    local.protocolo_json.aluno.responsavel_celular_ddi_snapshot = null;
  }
}

async function carregarResponsavelSnapshot() {
  try {
    ensureDefaults();

    const alunoSnap = local?.protocolo_json?.aluno;
    if (!alunoSnap) return;

    const jaTem =
      !!String(alunoSnap.responsavel_nome_snapshot || '').trim() ||
      !!String(alunoSnap.responsavel_celular_snapshot || '').trim();

    if (jaTem) return;

    if (local?.matricula_id) {
      const mat = await axios.get(`/operacional/matriculas/${local.matricula_id}`);
      const responsavelId = mat?.data?.responsavel_id ?? mat?.data?.responsavelId ?? null;

      if (responsavelId) {
        const pf = await axios.get(`/cadastros/pessoas-fisicas/${responsavelId}`);
        alunoSnap.responsavel_nome_snapshot = pf?.data?.nome || null;
        alunoSnap.responsavel_celular_snapshot = pf?.data?.celular || null;
        alunoSnap.responsavel_celular_ddi_snapshot = pf?.data?.celular ? (pf?.data?.celular_ddi || '+55') : null;
      }
      return;
    }

    if (local?.aula_avulsa && local?.aluno_id) {
      const r = await axios.get(`/operacional/aulas-avulsas/responsavel`, {
        params: { aluno_id: local.aluno_id }
      });

      alunoSnap.responsavel_nome_snapshot = r?.data?.responsavel_nome || null;
      alunoSnap.responsavel_celular_snapshot = r?.data?.responsavel_celular || null;
      alunoSnap.responsavel_celular_ddi_snapshot = r?.data?.responsavel_celular_ddi || null;
      return;
    }

    if (local?.aula_experimental && local?.aluno_nome) {
      try {
        const r = await axios.get(`/operacional/prospeccao/por-aluno`, {
          params: { nome_aluno: local.aluno_nome }
        });

        const nome = r?.data?.data?.nome || null;
        const tel = r?.data?.data?.telefone || null;
        const telDdi = r?.data?.data?.telefone_ddi || null;

        if (nome || tel) {
          alunoSnap.responsavel_nome_snapshot = nome;
          alunoSnap.responsavel_celular_snapshot = tel;
          alunoSnap.responsavel_celular_ddi_snapshot = telDdi;
          return;
        }
      } catch (e) {
        console.warn('[ProtocoloQueda] Falha ao buscar prospecção por aluno (vai tentar fallback):', e?.response?.status || e);
      }
    }

    if (!local?.chamada_id) return;

    const resp = await axios.get(`/operacional/chamadas/${local.chamada_id}`);
    const alunos = resp?.data?.alunos || [];
    if (!Array.isArray(alunos) || alunos.length === 0) return;

    const alvoNome = String(local?.aluno_nome || '').trim().toLowerCase();
    const alvoAnimal = Number(local?.animal_id || 0);
    const norm = (s) => String(s || '').trim().toLowerCase();

    const row =
      alunos.find((a) => norm(a?.aluno_nome) === alvoNome && Number(a?.animal_id || 0) === alvoAnimal) ||
      alunos.find((a) => norm(a?.aluno_nome) === alvoNome);

    if (!row) return;

    alunoSnap.responsavel_nome_snapshot =
      row?.responsavel_nome_matricula ||
      row?.responsavel_nome_avulsa ||
      row?.responsavel_nome_prospeccao ||
      null;

    alunoSnap.responsavel_celular_snapshot =
      row?.responsavel_celular_matricula ||
      row?.responsavel_celular_avulsa ||
      row?.responsavel_telefone_prospeccao ||
      null;

    alunoSnap.responsavel_celular_ddi_snapshot =
      row?.responsavel_celular_ddi_matricula ||
      row?.responsavel_celular_ddi_avulsa ||
      row?.responsavel_telefone_ddi_prospeccao ||
      null;
  } catch (e) {
    console.warn('Não foi possível carregar responsável (snapshot):', e);
  }
}

async function emitSave() {
  const payload = JSON.parse(JSON.stringify(local));
  let salvo;
  // Eventos Vue não aguardam o handler async do pai: use um contrato aguardável.
  if (props.persistir) {
    salvo = await props.persistir(payload, original);
  } else {
    salvo = await persistIfNeeded(payload);
  }
  Object.assign(local, JSON.parse(JSON.stringify(salvo)));
  original = snapshotProtocolo(local);
  emit('save', JSON.parse(JSON.stringify(local)));
  emit('salvou', { id: local.id, status: local.status });
}

async function persistIfNeeded(payload) {
  if (!props.saveToBackend || !local.id) throw new Error('Não foi possível identificar o protocolo para salvar.');
  const isVermelho = (local?.protocolo_json?.checklist?.alerta_vermelho?.itens || []).length > 0;
  const cs = local?.protocolo_json?.recepcao?.comunicado_supervisor;
  if (props.workflow && isVermelho && cs?.realizado && cs?.meio && cs.meio !== 'LIGACAO') {
    throw new Error('Alerta VERMELHO: comunicado ao supervisor deve ser por LIGAÇÃO.');
  }
  return persistirQueda(local, {
    ...payload,
    observacoes: local.protocolo_json?.descricao_ocorrido || local.observacoes || '',
    descricao: local.protocolo_json?.descricao_ocorrido || local.descricao || '',
    tipo_aluno: 'Queda',
    por: userName.value
  }, original);
}

const dict = {
  vermelho: [
    { code: 'VERMELHO_DOR_CABECA', label: 'Dor de cabeça' },
    { code: 'VERMELHO_PERDA_MEMORIA', label: 'Perda de memória' },
    { code: 'VERMELHO_DOR_PESCOCO', label: 'Dor no pescoço' },
    { code: 'VERMELHO_CONFUSAO_MENTAL', label: 'Confusão mental' },
    { code: 'VERMELHO_OLHAR_SEM_REACAO_PARALISADO', label: 'Olhar sem reação/paralisado' },
    { code: 'VERMELHO_DESMAIO', label: 'Desmaio' },
    { code: 'VERMELHO_DOR_COLUNA', label: 'Dor na coluna' },
    { code: 'VERMELHO_DOR_FORTE_QUADRIL_SACRO', label: 'Dor forte no quadril ou sacro' },
    { code: 'VERMELHO_DOR_INTENSA_QUALQUER_MEMBRO', label: 'Dor intensa em qualquer membro' },
    { code: 'VERMELHO_TONTURA_QUEDA_PRESSAO', label: 'Tontura e/ou queda de pressão' },
    { code: 'VERMELHO_DOR_FORTE_PEITO', label: 'Dor forte no peito' },
    { code: 'VERMELHO_LIMITACAO_QUALQUER_MOVIMENTO', label: 'Limitação de qualquer movimento' },
    { code: 'VERMELHO_FORMIGAMENTO_QUALQUER_MEMBRO', label: 'Formigamento em qualquer membro' },
    { code: 'VERMELHO_FRATURA_OU_FRATURA_EXPOSTA', label: 'Fratura ou fratura exposta' }
  ],
  amarelo: [
    { code: 'AMARELO_DOR_OMBRO', label: 'Dor no ombro' },
    { code: 'AMARELO_DOR_PE_OU_TORNOZELO', label: 'Dor no pé ou tornozelo' },
    { code: 'AMARELO_DOR_BRACO', label: 'Dor no braço' },
    { code: 'AMARELO_DOR_JOELHO', label: 'Dor no joelho' },
    { code: 'AMARELO_DOR_TORNOZELO', label: 'Dor no tornozelo' },
    { code: 'AMARELO_DOR_PUNHOS_OU_MAOS', label: 'Dor nos punhos ou mãos' }
  ],
  verde: [
    { code: 'VERDE_CAIU_EM_PE_SEM_DOR', label: 'Caiu em pé e não relata dor' },
    { code: 'VERDE_ESCORREGOU_ESTRIBO_CAVALO_PARADO_SEM_DOR', label: 'Escorregou do estribo e caiu com o cavalo parado e não relata dor' }
  ],
  naoMontar: [
    { code: 'NAO_MONTAR_BATEU_CABECA_CAPACETE', label: 'Bateu a cabeça (capacete)' },
    { code: 'NAO_MONTAR_CAIU_SENTADO', label: 'Caiu sentado' },
    { code: 'NAO_MONTAR_CAIU_DE_LADO_CAVALO_MOVIMENTO', label: 'Caiu de lado com o cavalo em movimento' },
    { code: 'NAO_MONTAR_OMBRO_NO_CHAO', label: 'Caiu com o ombro no chão' },
    { code: 'NAO_MONTAR_CORTE_OU_RALADO', label: 'Teve corte ou ralado' },
    { code: 'NAO_MONTAR_CAVALO_PISOU_OU_TROMBOU_AO_CAIR', label: 'Cavalo pisou ou trombou ao cair' },
    { code: 'NAO_MONTAR_LEVOU_MORDIDA', label: 'Levou uma mordida' },
    { code: 'NAO_MONTAR_LEVOU_COICE_DE_OUTRO_CAVALO', label: 'Levou um coice de outro cavalo' },
    { code: 'NAO_MONTAR_EFEITO_CHICOTE_PESCOCO', label: 'Sofreu um efeito chicote no pescoço' },
    { code: 'NAO_MONTAR_QUEDA_PRESSAO_QUALQUER_MOTIVO', label: 'Queda de pressão por qualquer motivo' },
    { code: 'NAO_MONTAR_CABECADA_DO_CAVALO', label: 'Cabeçada do cavalo' },
    { code: 'NAO_MONTAR_QUEDA_NA_CERCA_OU_MURO', label: 'Queda na cerca ou muro' },
    { code: 'NAO_MONTAR_REFUGO_TROMBAMENTO_OBSTACULO', label: 'Queda de refugo com trombamento no obstáculo' },
    { code: 'NAO_MONTAR_CAVALO_EMPINA', label: 'Queda do cavalo empinar' },
    { code: 'NAO_MONTAR_CAVALO_DISPARADO', label: 'Queda do cavalo disparado' }
  ],
  foraAula: [
    { code: 'FORA_AULA_MORDIDA', label: 'Mordida' },
    { code: 'FORA_AULA_COICE', label: 'Coice' },
    { code: 'FORA_AULA_CAVALO_PISAR_NO_ALUNO', label: 'Cavalo pisar no aluno' }
  ]
};
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.55);
  z-index: 20000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 12px;
}

.modal-container {
  background: #fff;
  width: 100%;
  max-width: 980px;
  max-height: 92vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border-radius: 8px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.35);
}

.modal-header {
  background: #e6e6e6;
  padding: 10px 14px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;
}

.modal-header h6 {
  margin: 0;
  font-weight: 700;
  font-size: 15px;
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.close-icon {
  font-size: 1.6rem;
  cursor: pointer;
  opacity: 0.8;
}

.close-icon:hover {
  opacity: 1;
}

.modal-content {
  padding: 14px 16px;
  overflow-y: auto;
  flex: 1;
  font-size: 14px;
}

.modal-footer {
  padding: 12px 16px;
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  flex-shrink: 0;
  border-top: 1px solid rgba(0, 0, 0, 0.08);
  background: #fafafa;
}

.pq-meta {
  background: rgba(0, 0, 0, 0.03);
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 10px;
  padding: 10px 12px;
  color: #444;
  font-size: 13px;
  line-height: 1.35;
}

.pq-meta strong {
  color: #222;
}

.modal-content hr {
  margin: 14px 0;
  opacity: 0.15;
}

.modal-content label {
  text-align: left !important;
}

.section-title {
  font-weight: 800;
  margin: 10px 0 8px;
  font-size: 14px;
}

.modal-header.alerta-vermelho {
  background: #c62828;
  color: #fff;
}

.modal-header.alerta-amarelo {
  background: #f9a825;
  color: #222;
}

.modal-header.alerta-verde {
  background: #2e7d32;
  color: #fff;
}

.modal-header.alerta-verde + .modal-content .alerta-banner {
  background: rgba(46, 125, 50, 0.10);
  border: 1px solid rgba(46, 125, 50, 0.25);
  color: #1b5e20;
}

.modal-header.alerta-roxo {
  background: #6a1b9a;
  color: #fff;
}

.modal-header.alerta-roxo + .modal-content .alerta-banner {
  background: rgba(106, 27, 154, 0.10);
  border: 1px solid rgba(106, 27, 154, 0.30);
  color: #4a148c;
}

.modal-header.alerta-vermelho .badge,
.modal-header.alerta-amarelo .badge {
  background: rgba(255, 255, 255, 0.25);
  color: inherit;
}

.alerta-banner {
  margin-bottom: 12px;
  padding: 10px 12px;
  border-radius: 8px;
  font-weight: 600;
  font-size: 13px;
}

.modal-header.alerta-vermelho + .modal-content .alerta-banner {
  background: rgba(198, 40, 40, 0.08);
  border: 1px solid rgba(198, 40, 40, 0.25);
  color: #b71c1c;
}

.modal-header.alerta-amarelo + .modal-content .alerta-banner {
  background: rgba(249, 168, 37, 0.12);
  border: 1px solid rgba(249, 168, 37, 0.35);
  color: #e65100;
}

.check-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px 14px;
  margin-bottom: 6px;
}

.check-item {
  display: flex;
  align-items: flex-start;
  justify-content: flex-start;
  gap: 8px;
  padding: 7px 8px;
  border: 1px solid rgba(0, 0, 0, 0.10);
  border-radius: 8px;
  background: #fff;
  cursor: pointer;
  user-select: none;
}

.check-item:hover {
  background: rgba(0, 0, 0, 0.02);
  border-color: rgba(0, 0, 0, 0.16);
}

.check-item input[type='checkbox'] {
  width: 16px;
  height: 16px;
  margin: 2px 0 0 0 !important;
  flex: 0 0 auto;
}

.check-item span {
  display: block;
  line-height: 1.22;
  color: #222;
  font-size: 13.5px;
}

.check-item input[disabled] + span {
  opacity: 0.6;
}

.check-item.is-checked {
  border-width: 2px;
  font-weight: 600;
}

.severidade-vermelho .check-item.is-readonly.is-checked {
  background: rgba(220, 53, 69, 0.15);
  border-color: #dc3545;
}

.severidade-amarelo .check-item.is-readonly.is-checked {
  background: rgba(255, 193, 7, 0.18);
  border-color: #ffc107;
}

.severidade-verde .check-item.is-readonly.is-checked {
  background: rgba(25, 135, 84, 0.15);
  border-color: #198754;
}

.severidade-roxo .check-item.is-readonly.is-checked {
  background: rgba(106, 27, 154, 0.15);
  border-color: #6a1b9a;
}

.check-item.is-readonly.is-checked {
  background: rgba(25, 135, 84, 0.12);
  border-color: rgba(25, 135, 84, 0.6);
}

.check-item.is-readonly.is-checked span {
  color: #111;
}

.check-item.is-readonly:not(.is-checked) {
  opacity: 0.7;
}

.required {
  color: red;
}

.form-group label {
  font-weight: 700;
}

.form-group textarea.form-control {
  font-size: 14px;
  line-height: 1.35;
  border-radius: 8px;
}

.pq-textarea {
  min-height: 130px;
  resize: vertical;
}

@media (max-width: 992px) {
  .modal-container {
    max-width: 940px;
  }

  .check-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 576px) {
  .modal-overlay {
    padding: 8px;
  }

  .modal-container {
    border-radius: 10px;
    max-height: 95vh;
  }

  .modal-content {
    padding: 12px;
  }

  .check-grid {
    grid-template-columns: 1fr;
  }

  .modal-footer {
    flex-wrap: wrap;
    justify-content: center;
  }
}

.tipo-aula-banner {
  margin-bottom: 8px;
  font-weight: 700;
  font-size: 14px;
}

.tipo-experimental {
  color: #2e7d32;
}

.tipo-avulsa {
  color: #f57c00;
}

.tipo-reposicao {
  color: #1565c0;
}
textarea.form-control {
  resize: vertical;
}
</style>